import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, User, ChevronDown, ChevronRight, Settings, ShoppingBag, Computer } from 'lucide-react';
import * as Types from '../../services/api/api.types';
import { apiService } from '../../services/api/instance';
import { useAuth } from '../../contexts/AuthContext';
import SearchBar from '../common/SearchBar';
import logger from '../../utils/logger';

const Navigation: React.FC = () => {
    const { user, isAuthenticated } = useAuth();
    const [categories, setCategories] = useState<Types.CategoryFull[]>([]);
    const [showProductsDropdown, setShowProductsDropdown] = useState(false);
    const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
    const [categoriesLoaded, setCategoriesLoaded] = useState(false);
    const navigate = useNavigate();
    const productsDropdownRef = useRef<HTMLDivElement>(null);
    const productsButtonRef = useRef<HTMLButtonElement>(null);
    const productsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        // Only fetch categories once
        if (categoriesLoaded) return;
        
        const fetchCategories = async () => {
            try {
                const data = await apiService.getCategories();
                setCategories(data);
                setCategoriesLoaded(true);
            } catch (error) {
                logger.error('Error loading categories:', error);
            }
        };

        fetchCategories();
    }, [categoriesLoaded]);

    const getBgName = (names: Types.Name[]): string => {
        const bgName = names.find(name => name.language_code === 'bg');
        return bgName ? bgName.text : '';
    };

    // Categories to exclude from the dropdown menu
    const excludedCategories = [
        'Дигитален ваучер за подарък',
        'Разопаковани продукти',
        'Услуги',
        'Компютърни системи'
    ];

    const parentCategories = categories.filter(cat => {
        const categoryName = getBgName(cat.name);
        return cat.parent === 0 && cat.show && !excludedCategories.includes(categoryName);
    });

    const getChildCategories = (parentId: number) => {
        return categories.filter(cat => cat.parent === parentId && cat.show);
    };

    const getCategorySlugOrId = (category: Types.CategoryFull): string =>
        category.slug ?? category.id.toString();

    const handleProductsMouseEnter = () => {
        if (productsTimeoutRef.current) {
            clearTimeout(productsTimeoutRef.current);
        }
        setShowProductsDropdown(true);
    };

    const handleProductsMouseLeave = () => {
        productsTimeoutRef.current = setTimeout(() => {
            setShowProductsDropdown(false);
        }, 150);
    };

    const handleProductsDropdownClick = () => {
        setShowProductsDropdown(false);
    };

    const toggleCategory = (categoryId: number, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const newExpanded = new Set(expandedCategories);
        if (newExpanded.has(categoryId)) {
            newExpanded.delete(categoryId);
        } else {
            newExpanded.add(categoryId);
        }
        setExpandedCategories(newExpanded);
    };

    return (
        <nav className="hidden lg:block sticky top-0 bg-white border-b border-gray-200 py-3 z-50">
            <div className="container mx-auto px-4 flex justify-between items-center">
                <div className="flex items-center space-x-8">
                    {/* Products Dropdown */}
                    <div className="relative">
                        <button
                            ref={productsButtonRef}
                            className="flex items-center text-sm font-medium hover:text-primary-500 transition-colors"
                            onMouseEnter={handleProductsMouseEnter}
                            onMouseLeave={handleProductsMouseLeave}
                        >
                            Продукти <ChevronDown className="ml-1 h-4 w-4" />
                        </button>

                        {showProductsDropdown && (
                            <div
                                ref={productsDropdownRef}
                                className="absolute top-full left-0 mt-1 bg-white shadow-xl rounded-lg z-50 border border-gray-200"
                                onMouseEnter={handleProductsMouseEnter}
                                onMouseLeave={handleProductsMouseLeave}
                                style={{
                                    width: '350px',
                                    maxHeight: '500px'
                                }}
                            >
                                <div className="py-2 overflow-y-auto max-h-[496px]">
                                    {parentCategories.map(parent => {
                                        const childCategories = getChildCategories(parent.id);
                                        const isExpanded = expandedCategories.has(parent.id);

                                        return (
                                            <div key={parent.id} className="border-b border-gray-100 last:border-b-0">
                                                {childCategories.length > 0 ? (
                                                    <>
                                                        <button
                                                            onClick={(e) => toggleCategory(parent.id, e)}
                                                            className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-gray-50 transition-colors"
                                                        >
                                                            <span className="text-sm font-medium text-gray-700">
                                                                {getBgName(parent.name)}
                                                            </span>
                                                            <ChevronRight
                                                                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                                                                    isExpanded ? 'rotate-90' : ''
                                                                }`}
                                                            />
                                                        </button>

                                                        <div
                                                            className={`overflow-hidden transition-all duration-200 ease-in-out ${
                                                                isExpanded ? 'max-h-96' : 'max-h-0'
                                                            }`}
                                                        >
                                                            <div className="bg-gray-50 py-1">
                                                                {childCategories.map(child => (
                                                                    <Link
                                                                        key={child.id}
                                                                        to={`/category/${getCategorySlugOrId(child)}`}
                                                                        className="block text-sm text-gray-600 hover:text-primary-500 hover:bg-gray-100 py-1.5 pl-8 pr-4 transition-colors"
                                                                        onClick={handleProductsDropdownClick}
                                                                    >
                                                                        {getBgName(child.name)}
                                                                    </Link>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <Link
                                                        to={`/category/${getCategorySlugOrId(parent)}`}
                                                        className="w-full flex items-center px-4 py-2.5 text-left hover:bg-gray-50 transition-colors"
                                                        onClick={handleProductsDropdownClick}
                                                    >
                                                        <span className="text-sm font-medium text-gray-700">
                                                            {getBgName(parent.name)}
                                                        </span>
                                                    </Link>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* PC Configurations Link */}
                    <Link 
                        to="/konfiguratsii" 
                        className="flex items-center text-sm font-medium hover:text-primary-500 transition-colors"
                    >
                        <Computer className="mr-1 h-4 w-4" />
                        PC Конфигурации
                    </Link>

                    {/* Services Link */}
                    <Link 
                        to="/services" 
                        className="flex items-center text-sm font-medium hover:text-primary-500 transition-colors"
                    >
                        <Settings className="mr-1 h-4 w-4" />
                        Услуги
                    </Link>

                    {/* Admin Link - Only for admin users */}
                    {isAuthenticated && user?.isAdmin && (
                        <Link 
                            to="/admin" 
                            className="flex items-center text-sm font-medium hover:text-primary-500 transition-colors"
                        >
                            <ShoppingBag className="mr-1 h-4 w-4" />
                            Админ панел
                        </Link>
                    )}
                </div>

                <div className="flex-1 max-w-lg mx-6">
                    <SearchBar 
                        placeholder="Потърси продукт, категория или марка..."
                        showSuggestions={true}
                    />
                </div>

                <div>
                    <Link to="/profile" className="flex items-center text-sm hover:text-primary-500 transition-colors group">
                        <User className="mr-2 h-5 w-5 text-gray-600 group-hover:text-primary-500 transition-colors" />
                        {isAuthenticated && user ? user.firstName : 'Профил'}
                    </Link>
                </div>
            </div>
        </nav>
    );
};

export default Navigation;